import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    DateRangePicker,
    FormStore,
    humanizeString,
    InfiniteScroll,
    inject,
    Loader,
    NothingHere,
    TasksStore,
    Title,
} from 'Mri.Web.Common';
import * as React from 'react';
import { BsTv } from '@react-icons/all-files/bs/BsTv';
import { RouteComponentProps } from 'react-router';
import { DropdownItem, DropdownMenu, DropdownToggle, Form, FormGroup, UncontrolledButtonDropdown } from 'reactstrap';
import { defaultTableColumns } from '../../Account/AccountStore';
import { AppStore } from '../../Core/AppStore';
import { DisplayListViewModel } from '../../DisplayFilters/DisplayListViewModel';
import { ActiveDisplay } from '../../Displays/ActiveDisplay';
import { DisplayRow } from '../../Displays/DisplayRow';
import { ManageDisplayListCard } from '../../Displays/ManageDisplayListCard';
import { CsvStore } from '../CsvStore';

export interface IExportsViewParams {
    name: string;
}

export interface IExportsViewProps extends RouteComponentProps<IExportsViewParams> {}

@autoinject
@observer
export class ExportsView extends React.Component<IExportsViewProps> {
    public constructor(props: IExportsViewProps) {
        super(props);

        const startTime = this.formStore.fields.startTime.value;

        startTime.setMonth(startTime.getMonth() - 1);

        const endTime = this.formStore.fields.endTime.value;
        endTime.setDate(endTime.getDate() + 1);
        endTime.setMinutes(59);
        endTime.setSeconds(59);

        this.vm = new DisplayListViewModel({
            columns: [
                { option: 'Serial Number' },
                { option: 'Unit Serial Number' },
                { option: 'Site ID' },
                { option: 'IP Address' },
            ],
            removeCheckedOnChange: false,
        });
    }

    public async componentDidMount() {
        try {
            await this.vm.loadMoreAsync();
            await this.vm.loadContractsAsync();
        } catch (e: any) {
            this.appStore.setError(e);
        }
    }

    public render() {
        return (
            <div className="container-fluid pt-3">
                <Title>Exports</Title>

                <div className="row">
                    <div className="col-12 col-md-7 mb-3">
                        <ManageDisplayListCard vm={this.vm} title="All Displays" height={800} renderDisplayFilter />
                    </div>

                    <div className="col-12 col-md-5">
                        <div className="card">
                            <div className="card-header bg-btn-primary flex-items-center">
                                Exporting Displays
                                <UncontrolledButtonDropdown className="ms-auto">
                                    <DropdownToggle caret color="primary" className="btn-sm">
                                        {humanizeString(this.exportType)}
                                    </DropdownToggle>

                                    <DropdownMenu>
                                        <DropdownItem onClick={() => this.setExportType('config')}>Config</DropdownItem>

                                        <DropdownItem onClick={() => this.setExportType('telemetry')}>
                                            Telemetry
                                        </DropdownItem>

                                        <DropdownItem onClick={() => this.setExportType('events')}>Events</DropdownItem>

                                        <DropdownItem onClick={() => this.setExportType('softwareVersions')}>
                                            Software Versions
                                        </DropdownItem>

                                        <DropdownItem onClick={() => this.setExportType('displaySoftwareManager')}>
                                            Display Software Manager
                                        </DropdownItem>
                                    </DropdownMenu>
                                </UncontrolledButtonDropdown>
                                <button
                                    className="btn-sm btn btn-primary"
                                    disabled={this.vm.checkedDisplays.length <= 0}
                                    onClick={this.exportCsv}
                                >
                                    <Loader message="Exporting..." showSpinner loading={this.exporting} />

                                    {!this.exporting && <>Export {this.vm.checkedDisplays.length} Displays</>}
                                </button>
                            </div>
                            <div
                                className="card-body p-0"
                                style={{ overflowY: 'scroll', height: 600 }}
                                ref={this.setExportListContainer}
                            >
                                {(this.exportType == 'events' || this.exportType == 'telemetry') && (
                                    <Form inline className="p-2 bg-gray-light">
                                        <FormGroup>
                                            <DateRangePicker
                                                startDate={this.formStore.fields.startTime}
                                                endDate={this.formStore.fields.endTime}
                                                timezone="UTC"
                                            />
                                        </FormGroup>{' '}
                                    </Form>
                                )}
                                <InfiniteScroll
                                    throttle={100}
                                    threshold={300}
                                    hasMore={this.exportHasMore}
                                    container={this.exportListContainer}
                                    onLoadMore={this.exportLoadMore}
                                >
                                    <table className="table table-striped">
                                        <tbody>
                                            {this.vm.checkedDisplays.slice(0, this.exportScrollCount).map(d => (
                                                <DisplayRow
                                                    key={d.serialNumber}
                                                    display={d}
                                                    whitelist={defaultTableColumns}
                                                    renderAfter={this.renderRemoveLink}
                                                    history={this.props.history}
                                                    vm={this.vm}
                                                />
                                            ))}
                                        </tbody>
                                    </table>

                                    <NothingHere
                                        empty={!this.vm.checkedDisplays.length}
                                        icon={<BsTv />}
                                        label="No Displays Checked"
                                        loading={this.vm.busy}
                                        loadingMessage="Loading Displays..."
                                    />
                                </InfiniteScroll>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    @bind
    @action
    private setExportListContainer(r: HTMLDivElement | null) {
        this.exportListContainer = r;
    }

    @bind
    @action
    private setExportType(
        exportType: 'config' | 'telemetry' | 'events' | 'displaySoftwareManager' | 'softwareVersions'
    ) {
        this.exportType = exportType;
    }

    @bind
    private renderRemoveLink(display: ActiveDisplay) {
        return (
            <div>
                <a className="btn fw-bold float-end text-danger" onClick={() => this.vm.toggleCheck(display)}>
                    Remove
                </a>
            </div>
        );
    }

    @bind
    @action
    private exportLoadMore() {
        this.exportScrollCount += 40;
    }

    @bind
    @action
    private async refreshAsync() {
        this.vm.clearChecked();
        this.exportScrollCount = 40;

        await this.vm.reloadAsync();
    }

    @bind
    @action
    private async exportCsv(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();

        this.exporting = true;
        const task = this.tasksStore.startSessionTask('Export To CSV');

        try {
            switch (this.exportType) {
                case 'config':
                    await this.exportConfig();
                    break;
                case 'telemetry':
                    await this.exportTelemetry();
                    break;

                case 'events':
                    await this.exportEvents();
                    break;

                case 'softwareVersions':
                    await this.exportSoftwareVersions();
                    break;

                case 'displaySoftwareManager':
                    await this.exportDisplaySoftwareManager();
                    break;
            }
            task.complete();
        } catch (e: any) {
            task.error(e);
            return;
        } finally {
            this.exporting = false;
        }

        await this.refreshAsync();
    }

    @bind
    private async exportConfig() {
        await this.csvStore.exportConfigAsync({
            displaySerialNumbers: this.vm.checkedDisplays.map(d => d.serialNumber),
        });
    }

    @bind
    private async exportTelemetry() {
        await this.csvStore.exportTelemetryAsync({
            serialNumbers: this.vm.checkedDisplays.map(d => d.serialNumber),
            startTime: this.formStore.fields.startTime.value,
            endTime: this.formStore.fields.endTime.value,
        });
    }

    @bind
    private async exportEvents() {
        await this.csvStore.exportEventsAsync({
            serialNumbers: this.vm.checkedDisplays.map(d => d.serialNumber),
            startTime: this.formStore.fields.startTime.value,
            endTime: this.formStore.fields.endTime.value,
        });
    }

    @bind
    private async exportSoftwareVersions() {
        await this.csvStore.exportSoftwareVersionsAsync({
            displaySerialNumbers: this.vm.checkedDisplays.map(d => d.serialNumber),
        });
    }

    @bind
    private async exportDisplaySoftwareManager() {
        await this.csvStore.exportDisplaySoftwareManagerAsync({
            displaySerialNumbers: this.vm.checkedDisplays.map(d => d.serialNumber),
        });
    }

    @computed
    private get exportHasMore() {
        return this.exportScrollCount < this.vm.displays.length;
    }

    private formStore = new FormStore({
        startTime: new Date(),
        endTime: new Date(),
    });
    @observable
    private exportType: 'config' | 'telemetry' | 'events' | 'displaySoftwareManager' | 'softwareVersions' = 'config';
    @observable
    private exporting = false;
    @observable
    private exportScrollCount = 40;
    private exportListContainer: HTMLDivElement | null = null;
    private vm: DisplayListViewModel;
    @inject
    private appStore!: AppStore;
    @inject
    private csvStore!: CsvStore;
    @inject
    private tasksStore!: TasksStore;
}
